import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Star, Trophy, Crown, Zap, Calendar, MapPin, Users, Check, X, Sparkles } from 'lucide-react';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { useUserRole } from '@/hooks/useUserRole';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { highlightsService, Highlight, SubmitHighlightData } from '@/services/highlightsService';

export default function Highlights() {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [pendingHighlights, setPendingHighlights] = useState<Highlight[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitForm, setSubmitForm] = useState<SubmitHighlightData>({
    title: '',
    description: '',
    category: 'achievement',
    location: ''
  });
  
  const { user: unifiedUser } = useUnifiedUser();
  const { permissions } = useUserRole();
  const isAdmin = permissions.role === 'admin';

  useEffect(() => {
    loadHighlights();
  }, [isAdmin]);

  const loadHighlights = async () => {
    try {
      setLoading(true);
      
      // Fetch approved highlights for everyone
      const approvedHighlights = await highlightsService.getApprovedHighlights();
      setHighlights(approvedHighlights);

      // Fetch pending highlights for admins
      if (isAdmin) {
        const pending = await highlightsService.getPendingHighlights();
        setPendingHighlights(pending);
      }
    } catch (error) {
      console.error('Error loading highlights:', error);
      toast.error('Failed to load highlights');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitHighlight = async () => {
    if (!unifiedUser) {
      toast.error('Please log in to submit highlights');
      return;
    }

    if (!submitForm.title || !submitForm.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const result = await highlightsService.submitHighlight(
        submitForm,
        unifiedUser.id,
        unifiedUser.userType,
        unifiedUser.username
      );

      if (result.success) {
        toast.success('Your highlight has been submitted for review! 100 points deducted.');
        setShowSubmitDialog(false);
        setSubmitForm({
          title: '',
          description: '',
          category: 'achievement',
          location: ''
        });
        loadHighlights();
      } else {
        toast.error(result.error || 'Failed to submit highlight');
      }
    } catch (error) {
      console.error('Error submitting highlight:', error);
      toast.error('Failed to submit highlight');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayPalPayment = () => {
    toast.info('PayPal integration coming soon! For now, use points to submit highlights.');
  };

  const handleApprove = async (highlightId: string) => {
    const success = await highlightsService.approveHighlight(highlightId);
    if (success) {
      toast.success('Highlight approved!');
      loadHighlights();
    } else {
      toast.error('Failed to approve highlight');
    }
  };

  const handleReject = async (highlightId: string) => {
    const success = await highlightsService.rejectHighlight(highlightId);
    if (success) {
      toast.success('Highlight rejected');
      loadHighlights();
    } else {
      toast.error('Failed to reject highlight');
    }
  };

  const handleToggleFeatured = async (highlightId: string, currentFeatured: boolean) => {
    const success = await highlightsService.toggleFeatured(highlightId, !currentFeatured);
    if (success) {
      toast.success(currentFeatured ? 'Removed from featured' : 'Added to featured!');
      loadHighlights();
    } else {
      toast.error('Failed to update featured status');
    }
  };

  const getCategoryIcon = (category: Highlight['category']) => {
    switch (category) {
      case 'achievement': return <Trophy className="w-4 h-4" />;
      case 'game': return <Calendar className="w-4 h-4" />;
      case 'team': return <Users className="w-4 h-4" />;
      case 'player': return <Star className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category: Highlight['category']) => {
    switch (category) {
      case 'achievement': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'game': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'team': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'player': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const featuredHighlights = highlights.filter(h => h.featured);
  const communityHighlights = highlights.filter(h => !h.featured);

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-background pt-20">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-4 py-8 space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-2">
              <Crown className="w-8 h-8 text-yellow-500" />
              <h1 className="text-4xl font-bold text-foreground">Community Highlights</h1>
            </div>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Celebrate amazing moments, achievements, and stories from our community
            </p>
          </div>

          {/* Submit Section */}
          <Card className="bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Share Your Highlight
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                Got an amazing moment to share? Submit it to be featured in our community highlights!
              </p>
              <div className="flex gap-4 flex-wrap">
                <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                      Submit with Points (100 pts)
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Submit Your Highlight</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Title *</label>
                        <Input
                          placeholder="Amazing goal, championship win, etc..."
                          value={submitForm.title}
                          onChange={(e) => setSubmitForm({...submitForm, title: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Description *</label>
                        <Textarea
                          placeholder="Tell us about this amazing moment..."
                          value={submitForm.description}
                          onChange={(e) => setSubmitForm({...submitForm, description: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Category *</label>
                        <Select 
                          value={submitForm.category} 
                          onValueChange={(value: 'achievement' | 'game' | 'team' | 'player') => 
                            setSubmitForm({...submitForm, category: value})
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select a category" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="achievement">Achievement</SelectItem>
                            <SelectItem value="game">Game</SelectItem>
                            <SelectItem value="team">Team</SelectItem>
                            <SelectItem value="player">Player</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Location</label>
                        <Input
                          placeholder="Where did this happen?"
                          value={submitForm.location}
                          onChange={(e) => setSubmitForm({...submitForm, location: e.target.value})}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleSubmitHighlight} 
                          className="flex-1"
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit (100 pts)'}
                        </Button>
                        <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                <Button variant="outline" onClick={handlePayPalPayment}>
                  Pay with PayPal ($5)
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Admin Section - Pending Approvals */}
          {isAdmin && pendingHighlights.length > 0 && (
            <Card className="border-orange-500/20 bg-orange-500/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-500">
                  <Sparkles className="w-5 h-5" />
                  Pending Approvals ({pendingHighlights.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {pendingHighlights.map((highlight) => (
                    <div key={highlight.id} className="p-4 bg-background rounded-lg border">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={getCategoryColor(highlight.category)}>
                              {getCategoryIcon(highlight.category)}
                              {highlight.category.charAt(0).toUpperCase() + highlight.category.slice(1)}
                            </Badge>
                            <Badge variant="outline" className="text-orange-500 border-orange-500/30">
                              Pending
                            </Badge>
                          </div>
                          <h4 className="font-semibold">{highlight.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{highlight.description}</p>
                          <p className="text-xs text-muted-foreground mt-2">
                            By {highlight.author_name} • {new Date(highlight.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                            onClick={() => handleApprove(highlight.id)}
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                            onClick={() => handleReject(highlight.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Featured Highlights */}
          {featuredHighlights.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Crown className="w-6 h-6 text-yellow-500" />
                Featured Highlights
              </h2>
              <div className="grid gap-6">
                {featuredHighlights.map((highlight) => (
                  <Card key={highlight.id} className="border-2 border-yellow-500/20 bg-gradient-to-r from-yellow-500/5 to-orange-500/5">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Badge className={getCategoryColor(highlight.category)}>
                              {getCategoryIcon(highlight.category)}
                              {highlight.category.charAt(0).toUpperCase() + highlight.category.slice(1)}
                            </Badge>
                            <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                              <Crown className="w-3 h-3 mr-1" />
                              Featured
                            </Badge>
                          </div>
                          <CardTitle className="text-xl">{highlight.title}</CardTitle>
                        </div>
                        {isAdmin && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleToggleFeatured(highlight.id, highlight.featured)}
                          >
                            Remove Featured
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-muted-foreground">{highlight.description}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>By {highlight.author_name}</span>
                        <span>•</span>
                        <span>{new Date(highlight.created_at).toLocaleDateString()}</span>
                        {highlight.location && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {highlight.location}
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* All Highlights */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-foreground">Community Highlights</h2>
            {communityHighlights.length > 0 ? (
              <div className="grid gap-4">
                {communityHighlights.map((highlight) => (
                  <Card key={highlight.id} className="hover:border-primary/30 transition-colors">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <Badge className={getCategoryColor(highlight.category)}>
                            {getCategoryIcon(highlight.category)}
                            {highlight.category.charAt(0).toUpperCase() + highlight.category.slice(1)}
                          </Badge>
                          <CardTitle className="text-lg">{highlight.title}</CardTitle>
                        </div>
                        {isAdmin && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleToggleFeatured(highlight.id, highlight.featured)}
                          >
                            <Crown className="w-4 h-4 mr-1" />
                            Feature
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-muted-foreground">{highlight.description}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>By {highlight.author_name}</span>
                        <span>•</span>
                        <span>{new Date(highlight.created_at).toLocaleDateString()}</span>
                        {highlight.location && (
                          <>
                            <span>•</span>
                            <div className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {highlight.location}
                            </div>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Star className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No highlights yet. Be the first to share an amazing moment!</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
